/**
 * 账户内核 —— 余额派生 · `internal` 品类规格 · 信用卡账期（**B 域**）
 *
 * 依据：规格 **§1**（账户）· **§8**（信用卡）· **§10**（内部挂账）· **§22.6**（权威枚举）·
 * `DR-008`（账户体系 + 内部挂账模型）· `DR-016`（社保两级结构 / 方案丙资产口径）·
 * `DR-021` §6.1~§6.3（账户 7 → **8 类** · `ownerId` NOT NULL）· `DR-026`（负债方类数）·
 * B 域 PRD **§5.1**（账户管理）· **§5.2**（信用卡最小集）· 架构 **§4.1 / §4.2 / §4.3 / §5.4**。
 *
 * ## 🔴 为什么要单独一个「账户内核」（而不是把逻辑写在 `account.service.ts` 里）
 *
 * 本文件回答的三件事全部是**纯派生 / 纯校验**，且**两端都要**：
 *
 * | 能力 | 除服务端外谁还要 | 若各写一份会怎样 |
 * |---|---|---|
 * | 余额派生 | Web / 小程序（离线展示、乐观更新） | 「账户余额」出现两个数 —— 用户立刻发现 |
 * | `internal` 品类规格（可否为负 / 是否需周期规则） | Web / 小程序（表单联动、禁用态） | 前端允许配上后端拒绝的品类组合（`AC-B1.3` 失败） |
 * | 信用卡账期与应还 | Web / 小程序（账期卡片） | 两端算出不同的「本期应还」⇒ 用户按少的还，产生循环利息 |
 *
 * ## 🔴 本文件**不做**什么（防止同一机制两处各答一遍 —— 铁律 24）
 *
 * 1. **净资产口径不在本文件** —— `internal` 各品类的入表方向**唯一**由
 *    `nucleus/networth.ts` 的 `composeNetWorth()` 判定（架构 §5.4）。
 *    ⇒ 本文件的 `INTERNAL_CATEGORY_SPEC` **刻意不带任何"入资产/入负债"字段**：
 *    带了就是同一事实的第二份载体。
 * 2. **不取时钟、不做 I/O、不碰库** —— `today` 一律由调用方以**账本本地日**传入
 *    （架构 §4.5：交易日是日历事实，无时区）。
 * 3. **不判权限** —— 「改归属人仅 owner」在路由装饰器上（架构 §6.3 显式白名单）。
 *
 * ## ⚠️ 与 `nucleus/txn-row.ts` 的分工（两处都算「Σin − Σout」，为什么不算双实现）
 *
 * | 函数 | 作用域 | 输入 |
 * |---|---|---|
 * | `txn-row.netOf()` | **一笔交易内**的**一个账户** | 该笔的腿 |
 * | `deriveAccountBalance()`（本文件） | **一个账户的全部历史** | 该账户的所有腿 + 初始余额 |
 *
 * 两者的分组维度不同（交易内 vs 全时段），不是同一个问题的两次回答。
 */

import type { Cents, RatioE4 } from '../types/brand.js';
import { AccountType, ContraKind, InternalCategory, LoanSubtype, RepaymentMethod } from '../enums/account.js';
import { LegDirection } from '../enums/journal.js';
import { AccountPolicy } from '../constants/index.js';
import { addDays, addMonths, clampInt, daysBetween, fromYmd, lastDayOfMonth, parseDay } from './calendar.js';
import { ratioE4, sumCents, toCents } from './money.js';

// ═════════════════════════ `internal` 品类规格（13 个） ═════════════════════════

/**
 * 一个 `internal` 品类的**结构属性**。
 *
 * 🔴 刻意只有三个字段 —— 见文件头「本文件不做什么」第 1 条（净资产方向不在此）。
 */
export interface InternalCategorySpec {
  category: InternalCategory;
  /** 中文名（**唯一来源**：端侧不得各写一份品类名） */
  label: string;
  /**
   * 是否**允许负余额**。
   * 🔴 由品类**默认决定、用户不可改**（规格 §1 / `AC-B1.3`：防误配）。
   */
  allowNegative: boolean;
  /**
   * 是否**需要**关联周期规则（`accounts.scheduleRuleId`）。
   *
   * ⚠️ **"需要"≠"自动生成归本域"**：生成时机与幂等在 **F 域**（B 域 PRD §5.7 边界铁律），
   * 本表只声明"这一品类在结构上有没有周期驱动"。
   */
  requiresSchedule: boolean;
}

/**
 * `internal` **13 品类**规格表 —— 架构 §4.3 的三组（非社保 4 / 社保侧 6 / 虚拟侧 3）。
 *
 * ⚠️ 下面按架构 §4.3 的三组分节**只是为了可读**；`InternalCategory` 的枚举注释已明文
 * 「代码里禁止按这 3 组做分支」—— 分组不是口径，逐值才是。
 */
export const INTERNAL_CATEGORY_SPEC: Readonly<Record<InternalCategory, InternalCategorySpec>> = {
  // ── 非社保（4）──
  [InternalCategory.Payable]: {
    category: InternalCategory.Payable,
    label: '待缴',
    allowNegative: true,
    requiresSchedule: true,
  },
  [InternalCategory.Prepaid]: {
    category: InternalCategory.Prepaid,
    label: '预付卡',
    allowNegative: false,
    requiresSchedule: false,
  },
  [InternalCategory.Amortize]: {
    category: InternalCategory.Amortize,
    label: '待摊',
    allowNegative: false,
    requiresSchedule: true,
  },
  [InternalCategory.InsuranceSav]: {
    category: InternalCategory.InsuranceSav,
    label: '储蓄型保险·保费挂账',
    allowNegative: false,
    requiresSchedule: false,
  },

  // ── 社保 · 社保侧（6）──
  [InternalCategory.SocialPension]: {
    category: InternalCategory.SocialPension,
    label: '养老保险',
    allowNegative: false,
    requiresSchedule: false,
  },
  [InternalCategory.SocialPensionSup]: {
    category: InternalCategory.SocialPensionSup,
    label: '企业年金（补充养老）',
    allowNegative: false,
    requiresSchedule: false,
  },
  [InternalCategory.SocialMedical]: {
    category: InternalCategory.SocialMedical,
    label: '医疗保险（社保侧）',
    allowNegative: false,
    requiresSchedule: false,
  },
  [InternalCategory.SocialUnemploy]: {
    category: InternalCategory.SocialUnemploy,
    label: '失业保险',
    allowNegative: false,
    requiresSchedule: false,
  },
  [InternalCategory.SocialFund]: {
    category: InternalCategory.SocialFund,
    label: '公积金',
    allowNegative: false,
    requiresSchedule: false,
  },
  [InternalCategory.SocialFundSup]: {
    category: InternalCategory.SocialFundSup,
    label: '补充公积金',
    allowNegative: false,
    requiresSchedule: false,
  },

  // ── 社保 · 虚拟侧（3）──
  [InternalCategory.SocialMedicalCur]: {
    category: InternalCategory.SocialMedicalCur,
    label: '医保·当年',
    allowNegative: false,
    requiresSchedule: false,
  },
  [InternalCategory.SocialMedicalHist]: {
    category: InternalCategory.SocialMedicalHist,
    label: '医保·历年',
    allowNegative: false,
    requiresSchedule: false,
  },
  [InternalCategory.SocialFundVirtual]: {
    category: InternalCategory.SocialFundVirtual,
    label: '公积金虚拟户',
    allowNegative: false,
    requiresSchedule: false,
  },
};

/** 13 品类的全部取值（**顺序 = 架构 §4.3 的书写顺序**，用于表单与枚举审计） */
export const INTERNAL_CATEGORIES: readonly InternalCategory[] = Object.values(InternalCategory);

/** 取品类规格；未知品类返回 `undefined`（**不静默兜底成一个默认规格**） */
export function internalCategorySpec(category: string | null | undefined): InternalCategorySpec | undefined {
  if (category === null || category === undefined) return undefined;
  return INTERNAL_CATEGORY_SPEC[category as InternalCategory];
}

/**
 * 该账户**是否允许负余额** —— 唯一判定处（`AC-B1.3`）。
 *
 * - `type='internal'` ⇒ 由品类决定（仅 `payable` 为 `true`）；
 * - 其余类型 ⇒ **恒 `false`**。
 *
 * ⚠️ 为什么 `credit` / `loan` 也返回 `false`：这两类的"可以为负"是**类型自带的语义**
 * （余额 < 0 即应还 / 未偿本金），不是这个开关管的。`accounts.allowNegative` 列的语义
 * 是「**内部挂账品类**可否欠着」，把它对信用卡置 `true` 会让同一个标记承担两种含义。
 */
export function resolveAllowNegative(type: AccountType, category: string | null | undefined): boolean {
  if (type !== AccountType.Internal) return false;
  return internalCategorySpec(category)?.allowNegative === true;
}

// ═════════════════════════ 账户形态校验（唯一实现） ═════════════════════════

/** 校验用的账户形态（**只取与形态有关的字段** —— 名称 / 币种等由契约层管） */
export interface AccountShapeInput {
  type: AccountType;
  category: InternalCategory | null;
  contraKind: ContraKind | null;
  creditLimit: number | null;
  billingDay: number | null;
  repaymentDay: number | null;
  tailNumber: string | null;
  /** 🆕 期限块六项（`DR-044`）—— 见 {@link ContraTerms} */
  contraTerms: ContraTerms;
  /**
   * 🆕 **对手方**（跟谁）—— 仅 `type='contra'` 上有语义（`DR-046` D2）。
   *
   * 🔴 与 `contraTerms` 是**两个不同层级**：对手方是「身份」，期限块是「条件」。
   * 三层归属（`DR-046` D2）：`counterparty` + `dueDate` ⇒ 全部 `contra`；
   * 其余五项 ⇒ 仅 `contraKind='loan'`。
   */
  counterparty: string | null;
  /**
   * 🆕 **预付卡次卡**（`DR-057` D4）—— 只有 `internal(prepaid)` 能有；
   * 其余类型必须三列为空（由规则 13 判定）。省略 = 本账户没有次数概念。
   */
  pass?: PassTimes | null;
}

/**
 * 🆕 **预付卡次卡**（`DR-057` D4 · 批次 97）。
 *
 * 语义：一张预付卡登记「总次数」与「单次金额」，「用一次」= 扣一次单次金额 + `used + 1`。
 *
 * 🔴 **剩余次数是派生量**（`passRemaining()`），**不落库** —— 与余额同一条纪律
 * （`AC-B1.4`：不得有汇总 / 中间态列）。落一个 `remaining` 列就意味着
 * 「total / used / remaining」三者可能出现 `total ≠ used + remaining`。
 */
export interface PassTimes {
  /** 总次数（买了多少次）；`null` = 未登记次数（普通预付卡） */
  total: number | null;
  /** 单次金额（整数分）；`null` = 未登记 */
  unitAmount: number | null;
  /** 已用次数（只能由 `usePass()` 动作 +1；不由腿数派生 —— 一笔消费可能分多条腿） */
  used: number;
}

/** 「未登记次数」的唯一表达（所有调用方用它，而不是各自摊开写三个 null / 0） */
export const EMPTY_PASS_TIMES: Readonly<PassTimes> = { total: null, unitAmount: null, used: 0 };

/**
 * 剩余次数 = `total − used`；**未登记** ⇒ `null`（不是 0）。
 *
 * 🔴 `null` 与 `0` 必须区分：`0` = "一次都不剩了"（该提示续卡），
 * `null` = "这张卡没登记次数"（不该出现任何次数 UI）。
 */
export function passRemaining(pass: PassTimes | null | undefined): number | null {
  if (pass === null || pass === undefined || pass.total === null) return null;
  return Math.max(0, pass.total - pass.used);
}

/**
 * 🆕 贷款参数六项（`DR-044`）—— **全部可空**，`null` 表示"未填"。
 *
 * 单独抽成类型是让「服务端 / 端侧 / 测试」三处**用同一个形状**表达这六项，
 * 而不是各自摊平成六个散参数（铁律 24：同一事实一份载体）。
 */
export interface ContraTerms {
  dueDate: string | null;
  annualRateE4: number | null;
  terms: number | null;
  startDate: string | null;
  method: RepaymentMethod | null;
  subtype: LoanSubtype | null;
}

/** 空贷款参数（所有调用方"没有这六项"时的唯一表达） */
export const EMPTY_CONTRA_TERMS: Readonly<ContraTerms> = {
  dueDate: null,
  annualRateE4: null,
  terms: null,
  startDate: null,
  method: null,
  subtype: null,
};

/**
 * 校验账户形态，返回**问题清单**（空数组 = 通过）。
 *
 * 🔴 为什么返回清单而不是 `throw`：本函数要同时服务两条链路 ——
 * 服务端（要把它翻成带错误码的 `AppException`）与契约/内核测试（要断言"恰好这几条问题"）。
 * 若在此处 `throw`，服务端就只剩一个泛化的错误文案，用户看不出到底哪一项配错了。
 *
 * 规则表（架构 §4.2 `accounts` 冻结要点 + B 域 PRD §5.1.5）：
 *
 * | # | 规则 | 依据 |
 * |---|---|---|
 * | 1 | `type='internal'` ⇒ `category` 必填；且 **`contraKind` 必须为空** | 架构 §4.2：两列**互斥** |
 * | 2 | `type='contra'` ⇒ `contraKind` 必填；且 **`category` 必须为空** | 同上 |
 * | 3 | 其余 6 类 ⇒ **两列都必须为空** | 同上（"其余类型两列皆 NULL"） |
 * | 4 | `category` 必须是 13 品类之一（不能是任意字符串） | `AC-B1.2` |
 * | 5 | 信用卡四字段（额度 / 账单日 / 还款日 / 尾号）**只允许**出现在 `credit` | B 域 PRD §5.2.1 |
 * | 6 | 账单日 / 还款日 ⇒ `[1, 31]` 整数（且**必须成对**，只填一个算不出账期） | `AccountPolicy` 说明 |
 * | 7 | `creditLimit` 若非空 ⇒ **正整数**（0 元额度无意义，使用率分母为 0） | `AC-B2.8` |
 * | 8 | 🆕 期限块六项**分层**：`dueDate` 允许在所有 `type='contra'`；其余五项仅 `contraKind='loan'` | `DR-044` D1 + v1 实测（应收 / 应付也有到期日） |
 * | 9 | 🆕 `annualRateE4` 若非空 ⇒ **非负整数**且 ≤ `AccountPolicy.AnnualRateE4Max`（= 100%） | `DR-044` §5（防"万分之一当百分数填"） |
 * | 10 | 🆕 `terms` 若非空 ⇒ **正整数**且 ≤ `AccountPolicy.TermsMax`；🔴 **不是必填**（`null` = 未知，合法） | `DR-044` §5 第 2/3 项（允许留空 · 总期数） |
 */
export function checkAccountShape(input: AccountShapeInput): string[] {
  const issues: string[] = [];

  // ── 1~4：`category` / `contraKind` 的互斥与取值 ──
  if (input.type === AccountType.Internal) {
    if (input.category === null) issues.push('type=internal 必须指定 category（13 个内部品类之一）');
    else if (INTERNAL_CATEGORY_SPEC[input.category] === undefined) {
      issues.push(`未知的内部品类：${String(input.category)}`);
    }
    if (input.contraKind !== null) issues.push('type=internal 不得同时指定 contraKind（两列互斥）');
  } else if (input.type === AccountType.Contra) {
    if (input.contraKind === null) issues.push('type=contra 必须指定 contraKind（5 类之一）');
    if (input.category !== null) issues.push('type=contra 不得同时指定 category（两列互斥）');
  } else {
    if (input.category !== null) issues.push(`type=${input.type} 不得指定 category（仅 internal 使用）`);
    if (input.contraKind !== null) issues.push(`type=${input.type} 不得指定 contraKind（仅 contra 使用）`);
  }

  // ── 5：信用卡四字段只在 credit 上出现 ──
  const creditFields: readonly (readonly [string, unknown])[] = [
    ['creditLimit', input.creditLimit],
    ['billingDay', input.billingDay],
    ['repaymentDay', input.repaymentDay],
    ['tailNumber', input.tailNumber],
  ];
  if (input.type !== AccountType.Credit) {
    for (const [name, value] of creditFields) {
      if (value !== null && value !== undefined && value !== '') {
        issues.push(`${name} 仅适用于信用卡账户（type=credit）`);
      }
    }
  }

  // ── 6：账单日 / 还款日区间与成对出现 ──
  const dayFields: readonly (readonly [string, number | null])[] = [
    ['billingDay', input.billingDay],
    ['repaymentDay', input.repaymentDay],
  ];
  for (const [name, value] of dayFields) {
    if (value === null || value === undefined) continue;
    if (!Number.isInteger(value) || value < AccountPolicy.BillingDayMin || value > AccountPolicy.BillingDayMax) {
      issues.push(
        `${name} 必须是 ${AccountPolicy.BillingDayMin}–${AccountPolicy.BillingDayMax} 的整数日（超出会让 2 月的账期窗口变形）`,
      );
    }
  }
  const hasBilling = input.billingDay !== null && input.billingDay !== undefined;
  const hasRepayment = input.repaymentDay !== null && input.repaymentDay !== undefined;
  if (hasBilling !== hasRepayment) {
    issues.push('账单日与还款日必须同时提供（只填一个算不出账期与还款窗口）');
  }

  // ── 7：额度为正整数 ──
  if (input.creditLimit !== null && input.creditLimit !== undefined) {
    if (!Number.isInteger(input.creditLimit) || input.creditLimit <= 0) {
      issues.push('信用额度必须是大于 0 的整数分');
    }
  }

  // ── 8：🆕 期限块六项**分层**归属（`DR-044` D1 + v1 实测）──
  //
  // 🔴 **两层，不是一层**（这是实测 v1 数据后修正的一处）：
  //   · `dueDate` —— 只要 `type='contra'` 即可（**应收 / 应付也有到期日**：
  //     v1 实测 `con_ij6vw3fnjmefxiubgd15`（借给张瑞）`due_date=2026-08-18`、
  //     `con_ggv5l35zexc3to2qlwsb`（车贷）`due_date=2029-10-12`）；
  //   · 其余五项（利率 / 期数 / 起息日 / 还款方式 / 子类型）—— **仅 `contraKind='loan'`**
  //     （v1 实测：三项只在 3 条 `kind='loan'` 上有值）。
  const contra2 = input.type === AccountType.Contra;
  const loanOnly = contra2 && input.contraKind === ContraKind.Loan;
  if (!contra2 && input.contraTerms.dueDate !== null && input.contraTerms.dueDate !== undefined && input.contraTerms.dueDate !== '') {
    issues.push('dueDate 仅适用于往来款项账户（type=contra）');
  }
  const loanOnlyValues: readonly (readonly [string, unknown])[] = [
    ['annualRateE4', input.contraTerms.annualRateE4],
    ['terms', input.contraTerms.terms],
    ['startDate', input.contraTerms.startDate],
    ['method', input.contraTerms.method],
    ['subtype', input.contraTerms.subtype],
  ];
  if (!loanOnly) {
    for (const [name, value] of loanOnlyValues) {
      if (value !== null && value !== undefined && value !== '') {
        issues.push(`${name} 仅适用于「往来款项 + 贷款」（type=contra 且 contraKind=loan）`);
      }
    }
  }

  // ── 9：🆕 年利率为非负整数且不超上限（DR-044 §5）──
  const rate = input.contraTerms.annualRateE4;
  if (rate !== null && rate !== undefined) {
    if (!Number.isInteger(rate) || rate < 0 || rate > AccountPolicy.AnnualRateE4Max) {
      issues.push(
        `年利率必须是 0–${String(AccountPolicy.AnnualRateE4Max)} 的整数（万分之一标度：336 = 3.36%）`,
      );
    }
  }

  // ── 10：🆕 期数为正整数且不超上限；🔴 留空合法（DR-044 §5 第 2、3 项）──
  const terms = input.contraTerms.terms;
  if (terms !== null && terms !== undefined) {
    if (!Number.isInteger(terms) || terms < 1 || terms > AccountPolicy.TermsMax) {
      issues.push(`期数必须是 1–${String(AccountPolicy.TermsMax)} 的整数（总期数；未知请留空，不要填 0）`);
    }
  }

  // ── 11：🆕 对手方**仅在 `contra` 上**有语义（`DR-046` D2）──
  //
  // 🔴 判据是 `type`，**不是** `contraKind`：应收 / 应付 / 垫付 / 报销 / 贷款**五类都要记"跟谁"**。
  //    这与规则 8 里"其余五项仅 loan"不同层级 —— 别把两条并成一条（`DR-046` D2 的三层归属）。
  // ⚠️ 空字符串按"未填"处理（与契约层 `.transform('' → null)` 同一语义）：
  //    否则表单里"把对手方删空"会被这里拦成 400，而用户只是清空了一个可选字段。
  const cp = input.counterparty;
  if (!contra2 && cp !== null && cp !== undefined && cp !== '') {
    issues.push('对手方仅适用于往来款项账户（type=contra）');
  }

  // ── 12：🆕 对手方对 `contra` **必填**（`DR-047` D8 · `Q域PRD` §7 #1 原文：
  //        「对手方为空 ⇒ 必填（对手方是"关系"的标识）；缺失即拒绝保存」）──
  //
  // 🔴 为什么补这一条：批 81 落 `counterparty` 列时按"可空"实现，**漏了 §7 #1**
  //    （当时未登记）。本批纠正 —— 且**落在内核**，因此
  //    ① 新建（`POST`）缺它 ⇒ 400；② 编辑把已有值**清空**（`PATCH {counterparty: null}`）
  //    也 ⇒ 400；③ 编辑不碰它（值仍在）⇒ 照常通过。**一处判据覆盖三个入口**。
  if (contra2 && (cp === null || cp === undefined || cp === '')) {
    issues.push('往来款项账户必须填对手方（跟谁往来）');
  }

  // ── 13：🆕 预付卡次卡三列**仅在 `internal(prepaid)` 上**有语义（`DR-057` D4）──
  //
  // 🔴 与规则 5（信用卡四列）/ 规则 8（贷款六项）同处置：**列由形态解释语义**，
  //    其余类型给了值就是"用户以为这张卡能按次扣" ⇒ 必须拦，而不是静默丢弃。
  // ⚠️ `used = 0` 是 `NOT NULL DEFAULT 0`，**不算"给了值"** —— 判据是
  //    「`total` / `unitAmount` 非空」或「`used > 0`」。
  const pass = input.pass ?? null;
  const passTouched =
    pass !== null &&
    (pass.total !== null || pass.unitAmount !== null || pass.used !== 0);
  if (passTouched) {
    const isPrepaid = input.type === AccountType.Internal && input.category === InternalCategory.Prepaid;
    if (!isPrepaid) {
      issues.push(
        '「总次数 / 单次金额」仅适用于预付卡（type=internal 且 category=prepaid）'
          + ' —— 其它类型没有「按次扣」的语义',
      );
    } else if (pass !== null) {
      // ① 两项**必须成对**：只填一个 ⇒ 「用一次」时金额或次数缺失，动作做不出来
      const hasTotal = pass.total !== null;
      const hasUnit = pass.unitAmount !== null;
      if (hasTotal !== hasUnit) {
        issues.push('「总次数」与「单次金额」必须同时填写（只填一个算不出每次扣多少）');
      }
      if (hasTotal && (!Number.isInteger(pass.total) || (pass.total as number) < 1)) {
        issues.push('总次数必须是大于 0 的整数');
      }
      if (hasUnit && (!Number.isInteger(pass.unitAmount) || (pass.unitAmount as number) < 1)) {
        issues.push('单次金额必须是大于 0 的整数分');
      }
      if (!Number.isInteger(pass.used) || pass.used < 0) {
        issues.push('已用次数不能为负');
      }
      // ② 已用 > 总数 ⇒ 剩余次数为负（只能是把总次数改小了，或数据被外部改坏）
      if (hasTotal && Number.isInteger(pass.total) && pass.used > (pass.total as number)) {
        issues.push(`已用 ${String(pass.used)} 次，不能超过总次数 ${String(pass.total)}`);
      }
      // ③ 已经有已用次数却没有总次数 ⇒ 剩余次数无从计算
      if (pass.used > 0 && !hasTotal) {
        issues.push('已有使用记录，必须填写总次数');
      }
    }
  }

  return issues;
}

// ═════════════════════════════ 余额派生 ═════════════════════════════

/** 参与余额派生的一条腿（最小子集 —— 只有方向与金额） */
export interface BalanceLeg {
  direction: LegDirection;
  /** **恒为正整数分**（规格 §22.9：绝不用负数 `out`） */
  amount: Cents;
}

/**
 * 账户余额 = `初始余额 + Σ(in 腿) − Σ(out 腿)`（同币种，架构 §4.1 第 1 条派生铁律）。
 *
 * 🔴 **三个"不"**：
 * 1. **不折算** —— 余额永远是**原币**；跨币种相加在 v2 是明确禁止的（`DR-004`）；
 * 2. **不落库** —— 全库不得有 `accounts.balance` 这类汇总列（`AC-B1.4` 数据审计项）；
 * 3. **不排除任何腿** —— 「不计收支」只影响**统计**，不影响**余额**：
 *    转账两条腿都要改变各自账户的余额（这正是转账的意义）。
 *
 * ## ⚠️ 服务端 SQL 必须与本函数**互为化身**
 * 服务端的集合查询用一条聚合表达同一公式：
 * `SUM(CASE WHEN direction = 'in' THEN amount ELSE -amount END)`（按 `accountId` 分组）
 * ⇒ 集成测试中以**同一批腿**分别跑 SQL 与本函数并断言相等（"同一判据两处各答一遍"的防线）。
 */
export function deriveAccountBalance(initialBalance: number, legs: readonly BalanceLeg[]): Cents {
  const ins: Cents[] = [];
  const outs: Cents[] = [];
  for (const leg of legs) {
    if (leg.direction === LegDirection.In) ins.push(leg.amount);
    else outs.push(leg.amount);
  }
  // 🔴 公式只在 `composeBalance()` 里写一次（本函数是它的**便捷包装**，不是第二份实现）
  return composeBalance({
    initialBalance,
    anchorAmount: null,
    netAll: (sumCents(ins) as number) - (sumCents(outs) as number),
    netAfterAnchor: 0,
  });
}

// ═══════════════════ 余额组合（含校准锚点 · `DR-056` D5 · 批次 96） ═══════════════════

/**
 * 余额组合的**输入聚合**（不是腿列表 —— 服务端只取 `SUM`，不取全量腿）。
 *
 * 🔴 **本接口与 `composeBalance()` 是余额公式的唯一权威**：
 * 加了校准锚点后，若还按原样在服务层抄 5 遍 `initialBalance + net`，
 * 就是 5 份会分叉的实现（铁律 24）。⇒ 服务层一律调本函数。
 */
export interface BalanceComposition {
  /** 期初余额（建账户时录入的事实值，整数分） */
  initialBalance: number;
  /** 锚点目标余额；`null` = 未锁定（退回期初口径） */
  anchorAmount: Cents | null;
  /** **全部**腿的净额 = Σ(in) − Σ(out)（同币种） */
  netAll: number;
  /** 锚点腿**之后**的腿净额；未锁定时传 `0`（本字段会被忽略） */
  netAfterAnchor: number;
}

/**
 * 账户余额 —— **唯一实现**（`DR-056` D5）。
 *
 * ```
 * 未锁定：余额 = initialBalance + Σ全部腿净额
 * 已锁定：余额 = anchorAmount  + Σ(锚点腿之后的腿净额)
 * ```
 *
 * ## 🔴 「锁定」改变的是什么（别理解成"改余额"）
 *
 * 它只改变**从哪条腿开始累加**，不改任何金额：
 * 校准时仍然生成一笔调整腿（`excludeFromStats=1`），差额永远躺在流水里可审计。
 * 所以 `AC-B6.7`「无直接改账户余额路径」**依然成立**。
 *
 * ## ⚠️ 已知代价（`DR-056` D7，不是 bug）
 *
 * 锁定之后修改**锚点之前**的交易：那笔的金额仍进流水与报表，但余额不动 ⇒
 * 「报表支出合计」与「现金减少」脱节，且没有一条记录解释这个差。
 * 这是「以记录时为准」这个需求的本质代价 —— 界面必须显式告知，不能沉默提供。
 *
 * ## ⚠️ 服务端 SQL 必须与本函数**互为化身**
 *
 * 服务端用一条聚合表达 `netAfterAnchor`（按 `(date, createdAt, seq)` 全序过滤后求和），
 * 与本函数共用 `isAfterAnchor()` 的判据 ⇒ 集成测试以同一批腿两边对账。
 */
export function composeBalance(input: BalanceComposition): Cents {
  if (input.anchorAmount === null) {
    return toCents(input.initialBalance + input.netAll);
  }
  return toCents((input.anchorAmount as number) + input.netAfterAnchor);
}

/**
 * 一条腿在**全序**里的位置 —— 判定"在锚点之前还是之后"用。
 *
 * 🔴 三级排序缺一不可（`DR-056` D5）：
 * - 只看 `date` ⇒ 锚点**当天**的其它交易无法归位（同一天跨不过去）；
 * - 只看 `createdAt` ⇒ 用户**补录**一笔日期在锚点之前的交易会被当成"之后"，
 *   于是余额会变 —— 而方式 2 的定义恰恰是"改调整之前的金额，余额不动"。
 */
export interface LegPosition {
  /** 交易日（账本本地日历日 `YYYY-MM-DD`） */
  date: string;
  /** 交易创建时刻（UTC 毫秒） */
  createdAt: number;
  /** 笔内序号（`legs.seq`，0 起） */
  seq: number;
}

/**
 * `a` 是否**严格在**锚点之后（全序比较：`date` → `createdAt` → `seq`）。
 *
 * 相等 ⇒ `false`（锚点腿自身不算"之后"，它的金额已含在锚点值里）。
 */
export function isAfterAnchor(a: LegPosition, anchor: LegPosition): boolean {
  if (a.date !== anchor.date) return a.date > anchor.date;
  if (a.createdAt !== anchor.createdAt) return a.createdAt > anchor.createdAt;
  return a.seq > anchor.seq;
}

/**
 * 🆕 **该账户类型是否允许「校准余额」**（`DR-056` D3 · 负责人 2026-10-22 拍板）。
 *
 * 🔴 只放三类「余额 = 钱有多少」的账户。其余类型余额含义不同、且各有专门机制：
 *
 * | 类型 | 余额含义 | 已有的正规机制 |
 * |---|---|---|
 * | `credit` | 负数 = 欠款 | 「还款」（批 94，转账 2 腿不计收支） |
 * | `loan` | 负数 = 未还本金 | 「还本期」（`DR-049`，按计划表派生） |
 * | `internal` | 预付/待摊/社保 | 充值、摊销、缴费（`DR-016` / `DR-008`） |
 * | `contra` | 应收应付 | 结算 / 报销 / 还款（`DR-047`/`048`/`049`） |
 *
 * ⇒ 在那些类型上也允许校准 = 给同一件事开**第二条改余额的路**，
 * 且那条路不受既有机制约束（例如把贷款余额手工改小，还款计划表立刻与实际不符）。
 *
 * 🔴 判据只在这里写一次：服务端用它拦请求，端侧用它决定**画不画按钮**
 * （服务端仍要拦 —— 端侧不给按钮只是"不给犯错的机会"，不是权限）。
 */
export function isBalanceAdjustable(type: AccountType): boolean {
  return type === AccountType.Cash || type === AccountType.Debit || type === AccountType.Ewallet;
}

// ═════════════════════════════ 信用卡账期 ═════════════════════════════

/**
 * 「该日所在月的第 `dom` 天」，超出该月天数时取**月末**。
 *
 * 🔴 `2026-10-12` 起 `BillingDayMax = 31` ⇒ **本分支是常用路径，不再是防御性分支**：
 * `dom = 31` 在 2 月得 28/29、在 30 天月得 30 —— 正是「月末出账」（花呗 / 白条 / 美团 / 抖音）的语义。
 *
 * 🆕 `DR-060`：导出给 `nucleus/installment.ts` 用 —— 分期期次的到期日必须与
 * `computeCreditBill` 的账单日**同一实现**（逐月 + 短月 clamp），否则账单扣减对不上期次。
 */
export function dayInMonth(day: string, dom: number): string {
  const { y, m } = parseDay(day);
  return fromYmd(y, m, clampInt(dom, 1, lastDayOfMonth(y, m)));
}

/**
 * **最近一次** `dom` 日（`≤ today`）。
 *
 * 例：`today = 2026-03-02`、`dom = 20` ⇒ `2026-02-20`（本月 20 日还没到）。
 */
function lastOnOrBefore(day: string, dom: number): string {
  const thisMonth = dayInMonth(day, dom);
  return thisMonth <= day ? thisMonth : dayInMonth(addMonths(day, -1), dom);
}

/**
 * **下一次** `dom` 日（`> today`）。
 *
 * 例：`today = 2026-03-20`、`dom = 20` ⇒ `2026-04-20`（"下一次"不含今天）。
 *
 * 🆕 `DR-067`（批 `116`）：**导出**给「记账时分期」用 —— 首期账单日 = 严格晚于记账日的
 * 最近一个账单日。它与本文件的 **还款日推算**（`computeCreditBill`）是同一个语义，
 * 消费侧**不得**再拼一遍 `dayInMonth(addMonths(...))`（拼出来的第二个版本迟早与这里分叉，
 * 而分叉的表现是"分期扣减与账单期错开"，只能靠对账发现）。
 */
export function nextAfter(day: string, dom: number): string {
  const thisMonth = dayInMonth(day, dom);
  return thisMonth > day ? thisMonth : dayInMonth(addMonths(day, 1), dom);
}

/** 信用卡余额的**呈现档位**（B 域 PRD §5.2.3 的四种情形，加一个"已结清"） */
export const CreditStanding = {
  /** 余额 = 0：本期已结清 */
  Clear: 'clear',
  /** 余额 > 0：**溢缴款**（资产，非收入） */
  Overpaid: 'overpaid',
  /** 余额 < 0 且未临近还款日：**应还**（⚠️ **不得使用支出绿**，`AC-B2.6` 红线） */
  Due: 'due',
  /** 余额 < 0 且距还款日 ≤ `AccountPolicy.DueSoonDays` 天：琥珀提示 */
  DueSoon: 'dueSoon',
  /** 余额 < 0 且**已过本期还款日**：朱红（破坏性状态） */
  Overdue: 'overdue',
} as const;
export type CreditStanding = (typeof CreditStanding)[keyof typeof CreditStanding];

/**
 * 信用卡档位 —— 唯一判定处。
 *
 * @param repaymentDate 本期**还款日**（由 {@link computeCreditBill} 给出）；`null` = 未配置账期
 * @param today         账本本地日
 *
 * ⚠️ **实现层细化（本批登记）**：B 域 PRD §5.2.3 只给了四行**中文语义**（应还 / 临近 / 逾期 / 溢缴），
 * 未规定"逾期"的判定表达式。本实现取「**已过本期还款日且仍有应还**」——
 * 若已还清（余额 ≥ 0）则不判逾期（那是"上一期已结清"，不是破坏性状态）。
 */
export function creditStanding(balance: Cents, repaymentDate: string | null, today: string): CreditStanding {
  const n = balance as number;
  if (n > 0) return CreditStanding.Overpaid;
  if (n === 0) return CreditStanding.Clear;
  if (repaymentDate === null) return CreditStanding.Due;
  if (today > repaymentDate) return CreditStanding.Overdue;
  return daysBetween(today, repaymentDate) <= AccountPolicy.DueSoonDays
    ? CreditStanding.DueSoon
    : CreditStanding.Due;
}

/** 使用率档位（B 域 PRD §5.2.4）：常规品牌色 / 琥珀 / 朱红 */
export const CreditUsageLevel = {
  Normal: 'normal',
  Notice: 'notice',
  Over: 'over',
} as const;
export type CreditUsageLevel = (typeof CreditUsageLevel)[keyof typeof CreditUsageLevel];

/**
 * 使用率 = `|余额| / creditLimit`（万分之一整数）。
 *
 * 🔴 **额度未填 ⇒ 返回 `null`，不是 0** —— 0 会被画成"一点没用"的进度条，
 * 而事实是"没法算"（B 域 PRD §6.3 C3 明确："进度条**不显示**（不报错）"）。
 * 余额 ≥ 0（溢缴 / 结清）同样返回 `null`：没有"应还占比"可言。
 */
export function creditUsageE4(balance: Cents, creditLimit: number | null): RatioE4 | null {
  const n = balance as number;
  if (n >= 0) return null;
  if (creditLimit === null || creditLimit === undefined || creditLimit <= 0) return null;
  return ratioE4(toCents(-n), toCents(creditLimit));
}

/** 把使用率翻成档位；`null`（算不出）⇒ 返回 `null` ⇒ 进度条不显示 */
export function creditUsageLevel(usageE4: RatioE4 | null): CreditUsageLevel | null {
  if (usageE4 === null) return null;
  const u = usageE4 as number;
  if (u >= AccountPolicy.UsageOverE4) return CreditUsageLevel.Over;
  if (u >= AccountPolicy.UsageNoticeE4) return CreditUsageLevel.Notice;
  return CreditUsageLevel.Normal;
}

/** 账期计算入参 */
export interface CreditBillInput {
  /** 账单日（1–31；31 在短月按月末处理） */
  billingDay: number;
  /** 还款日（1–31；同上） */
  repaymentDay: number;
  /** 账本本地日 `YYYY-MM-DD` */
  today: string;
  /** 该卡（= 该账户）的**全部**腿；窗口外的会被忽略（🆕 含退款标记，见 {@link CreditBillLeg}） */
  legs: readonly CreditBillLeg[];
  /**
   * 还款优惠（B 域 PRD §5.2.2 公式里的 `waiver` · 批 `106-B` 接真值）。
   *
   * 由调用方（账户服务）从 `credit_bill_waivers` 按该期 `bill_day` 汇总注入；
   * 🔴 **未出账单不吃优惠**（与分期同则：调整只发生在"出账那一刻"，PRD §5.3 / D5）。
   */
  waiver?: Cents;
  /**
   * 🆕 信用卡分期对**本期账单**的成对调整（`DR-060` §4.3 · `AC-B2.14`）。
   * 未传 = 该卡没有需要落在本期账单日上的分期（原口径，向后兼容）。
   */
  installment?: CreditBillInstallment;
  /**
   * 🆕 **上期余额**（`DR-064` D1/D3 · 批 `113`）—— 上一期账单金额（**未减该期还款**）。
   *
   * 可为负 = 溢缴款（上期多还的部分带到本期继续抵扣）。
   *
   * 🔴 **只由 `computeCreditBillHistory()` 滚动注入**：单期调用不传 = 0（只看这一期）。
   * 端侧与服务层**不得自己算**（铁律 24）—— 那会得到"本期消费净额"而不是"现在欠多少"。
   */
  openingBalance?: Cents;
}

/**
 * 🆕 账单窗口内的一条腿（{@link BalanceLeg} + 窗口计算需要的两个事实）。
 *
 * - `date`：交易日期（腿表没有日期列，来自 `transactions.date`）；
 * - `refund`：🆕 **是否退款冲减腿**（`DR-060` §6 修复的分流标记）。
 *   判据与内核 {@link isRefundTxn} 同源（`in` 腿 + 挂支出分类 + **计收支**），
 *   由调用方（账户服务的取数层）逐腿判定后传入；
 *   🔴 **还款转账**（`excludeFromStats` / 无支出分类）**不得**打此标记 ——
 *   打了会把还款当退款、从「本期应还」里错误冲减（用户按少的还 → 循环利息）。
 *   `undefined` / `false` = 不是退款（**默认非退款**：宁可多还一分，不可少还一分）。
 */
export type CreditBillLeg = BalanceLeg & {
  date: string;
  refund?: boolean;
  /**
   * 🆕 **还款优惠腿**（`legs.linked_waiver_id` 非空 · `DR-061` 四类归属第一类）。
   *
   * 🔴 **不计入「本期流入」**（`DR-064` D1）：优惠是"少还的钱"，账上没有资金进来；
   * 它在公式里是独立减项（`waiver`），并进流入再单列减就是**双计**。
   */
  waiver?: boolean;
  /**
   * 🆕 **分期本金腿**（交易命中 `loan_schedules.paid_transaction_id` · `DR-061` 第三类）。
   *
   * 🔴 **不计入「本期流入」**（`DR-064` B4）：它已经在 `installment.principalDeducted`
   * 里从本期账单移出过一次；再算作流入就是**同一笔钱减两遍**。
   */
  principal?: boolean;
};

/** 🆕 分期对当期账单的**成对调整**（`DR-060` §4.3 · `AC-B2.14`） */
export interface CreditBillInstallment {
  /**
   * 被分期移出本期的本金 = Σ（`start_bill_day` == 本期账单日 的 **active** 计划）`principal_total`。
   * 银行已把这笔钱移出本期账单 ⇒ 从「本期消费」里扣掉（AC-B2.14 的"− 分期本金"）。
   */
  principalDeducted: Cents;
  /**
   * 本期分期应还 = Σ（`due_date` == 本期账单日 且 **`pending`** 的期次）本金 + 手续费。
   * 🔴 只数 **`pending`**：已还期次的钱已实际流过（转账腿），再进 `due` 会误导用户重复还款
   * （提前还款 ⇒ 该期已 `paid` ⇒ 本期应还自然不再含它）。
   */
  dueThisPeriod: Cents;
}

/**
 * 🆕 **某一期账单的还款状态**（批 `106-B` · `AC-B2.59`）。
 *
 * 🔴 三态由**数值**推导（剩余 / 已还），不是自由文本 —— 唯一实现在 {@link creditPaidStatusOf}。
 * ⚠️ 与 `CreditStanding`（卡级余额档位）**互不推导**（`AC-B2.59` 后半句 · 铁律 24）：
 * `standing=clear` 推不出「每一期都 paid」；`paidStatus=paid` 也推不出 `standing`。
 */
export type CreditPaidStatus = 'paid' | 'partial' | 'pending';

/** 🆕 一期账单的「已还 / 剩余 / 状态」三件套（`AC-B2.58` 公式） */
export interface CreditBillPaid {
  /**
   * 已还 = **分配到该期的实付还款** + 该期优惠（规格「实付 + 减免」）。
   * 🔴 分配 = 还款腿按时间优先冲抵最早未结清期（{@link allocateRepayments}）；
   * 分期本金还款腿**不计入**（它还的是分期本金，判据见服务端四类归属）。
   */
  repaid: Cents;
  /** 剩余 = `max(0, due − repaid)` */
  remaining: Cents;
  status: CreditPaidStatus;
}

/** 三态判定（**唯一实现** —— 端侧不得再写一份 if/else） */
export function creditPaidStatusOf(due: Cents, repaid: Cents): CreditPaidStatus {
  const remaining = ((due as number) - (repaid as number)) > 0 ? toCents((due as number) - (repaid as number)) : toCents(0);
  if (remaining === 0) return 'paid';
  if ((repaid as number) > 0) return 'partial';
  return 'pending';
}

/** 🆕 参与还款分配的一条实付还款腿（**已剔除**退款 / 优惠 / 分期本金腿 —— 四类归属在服务端做完） */
export interface CreditRepaymentLeg {
  /** 交易日期（账本本地日） */
  date: string;
  /** 交易创建毫秒 —— 同日多笔时后写的在后（`(date, createdAt)` 升序） */
  createdAt: number;
  /** 实付金额（整数分，> 0） */
  amount: Cents;
}

/**
 * 🆕 **还款分配：时间优先冲抵最早未结清期**（批 `106-B` · `AC-B2.57`）。
 *
 * ⚠️ **批 `113` 起账单不再用它**（`DR-064` D4）：新口径下"这笔还款算哪一期"由**窗口**天然回答
 * —— 落在哪一期的 `[账单日, 下一期账单日)` 里就是那一期的已还，不需要跨期冲抵。
 * 函数保留是因为它本身是独立成立的口径（`AC-B2.61` 五场景反例钉仍在测），
 * 将来要做「逾期补还冲抵」时是现成实现；🔴 **任何人不得把它重新塞回账单链路**
 * （那会与 `inflow` 里已计入的还款**双计**）。
 *
 * - 还款腿按 `(date, createdAt)` **升序**，依次冲抵**最早未结清**的账单期（`due` 未被填满的期）；
 * - 填满一期再填下一期；全部结清后仍有余 ⇒ **溢缴款**（`overpaid`，不归任何期）。
 *
 * 🔴 `periods` 须为**时间升序**（最早在前）；`due` 已含优惠 / 分期调整（即对外的"该期应还"）。
 *
 * @returns `byBillDay` = 分配到各期的实付合计（未命中不出键）；`overpaid` = 溢缴
 */
export function allocateRepayments(
  periods: readonly { billDay: string; due: Cents }[],
  repaymentLegs: readonly CreditRepaymentLeg[],
): { byBillDay: Readonly<Record<string, Cents>>; overpaid: Cents } {
  const orderedLegs = [...repaymentLegs].sort((a, b) =>
    a.date === b.date ? a.createdAt - b.createdAt : a.date < b.date ? -1 : 1,
  );
  const byBillDay: Record<string, Cents> = {};
  const remainingDue = new Map(periods.map((p) => [p.billDay, p.due as number]));
  let leftover = 0;
  for (const leg of orderedLegs) {
    let left = leg.amount as number;
    for (const p of periods) {
      if (left <= 0) break;
      const rem = remainingDue.get(p.billDay) ?? 0;
      if (rem <= 0) continue;
      const take = left < rem ? left : rem;
      remainingDue.set(p.billDay, rem - take);
      byBillDay[p.billDay] = toCents(((byBillDay[p.billDay] as number | undefined) ?? 0) + take);
      left -= take;
    }
    leftover += left;
  }
  return { byBillDay, overpaid: toCents(leftover) };
}

/** 信用卡当期账单（B 域 PRD §5.2.2 的公式逐项落地） */
export interface CreditBill {
  /** 消费窗口起点 = 上期账单日（**含**） */
  windowStart: string;
  /** 消费窗口终点 = 本期账单日（**不含**） */
  windowEnd: string;
  /** 本期消费（窗口内 `out` 腿合计） */
  consumed: Cents;
  /**
   * 窗口内**退款**冲减腿合计 —— 🆕 `DR-060` §6 起**只数退款形态的 `in` 腿**
   * （`in` + 支出分类 + 计收支，见 {@link CreditBillLeg.refund}）；
   * 还款转账**不再**被当成退款冲减（那是本函数 2026-10-24 前的既有缺陷）。
   *
   * 🆕 批 `113`：它是 {@link inflowRefund} 的同一个值（保留旧名给既有消费者）。
   */
  refundedInWindow: Cents;
  /**
   * 🆕 **上期余额**（`DR-064` D1）—— 上一期账单金额结转过来的欠款；负 = 溢缴款。
   */
  openingBalance: Cents;
  /**
   * 🆕 **本期流出** = 窗口内 `out` 腿合计（与 {@link consumed} **同一个值**，
   * 新名字用于对账块那行「本期流出」）。
   */
  outflow: Cents;
  /**
   * 🆕 **本期流入** = 窗口内 `in` 腿合计，**含退款与还款**，**不含**优惠腿与分期本金腿
   * （`DR-064` D1/B4）。恒等式：`inflow = inflowRefund + inflowRepayment`（`AC-B2.68`）。
   */
  inflow: Cents;
  /** 🆕 流入里的**退款**部分（`= refundedInWindow`） */
  inflowRefund: Cents;
  /** 🆕 流入里的**还款**部分（其余计资金的 `in` 腿） */
  inflowRepayment: Cents;
  /**
   * 🆕 **结转给下一期的余额** = 未 clamp 的原始金额（可为负 = 溢缴款）。
   *
   * 🔴 **不进契约**（`DR-064` D3）：它是内核滚动结转的中间量，用户只看 {@link due}。
   */
  carryover: Cents;
  /**
   * 本期金额 = `max(0, openingBalance + outflow − inflow − waiver − 分期移出本金 + 本期分期应还)`
   * （`DR-064` D1 · 替换 `AC-B2.44` 的旧式）。
   */
  due: Cents;
  /**
   * 🆕 该期**实际计入公式**的还款优惠（批 `106-B` · `AC-B2.44`）。
   * 0 / 无优惠 ⇒ **不出键**（可选键剔键纪律）。展示净额法（`AC-B2.52`）时读它。
   */
  waiverApplied?: Cents;
  /**
   * 🆕 已还 / 剩余 / 状态（批 `106-B` · `AC-B2.58 / B2.59`）。
   *
   * 🔴 **只由 `computeCreditBillHistory()` 在拿到还款腿后填**（单期 `computeCreditBill`
   * 的入参没有跨期分配信息，不出本键）。未出账单**永远没有**它（无 `due` ⇒ 无"该还多少"可言）。
   * ⚠️ `standing`（余额档位）与它**互不推导**（铁律 24 —— 两个问题、两套依据）。
   */
  paid?: CreditBillPaid;
  /**
   * 🆕 分期成对调整（`DR-060` §4.3）；调用方未传分期时**不出键**（可选键剔键纪律）。
   */
  installment?: CreditBillInstallment;
  /**
   * **退款剔除**：本期账单日之后（到今天）到账的**退款**冲减腿 —— **单列，不进上式**，
   * 记入下期账单（B 域 PRD §5.2.2「单列信息，不并入上式」）。
   * 🆕 同 `refundedInWindow`：只数退款形态（还款转账不计入）。
   */
  refundsAfterBillDay: Cents;
  /** 本期账单日 */
  billDay: string;
  /** 本期还款日 */
  repaymentDate: string;
  /** 还款窗口 = `[本期账单日, 本期还款日)` */
  repaymentWindowStart: string;
  /** 还款窗口终点（**不含**） */
  repaymentWindowEnd: string;
  /** 距还款日的天数（可负 = 已过） */
  daysUntilRepayment: number;
}

/**
 * 计算信用卡**当期账单**。
 *
 * ```
 * 本期应还 = 本期消费 − 账单日前的退款 − 小额优惠（waiver）− 分期移出本金 + 本期分期应还
 * 【单列】退款剔除 = 账单日后的退款（记入下期账单）
 * ```
 *
 * 🔴 **退款 vs 还款（`DR-060` §6 · 2026-10-24 修既有缺陷）**：窗口内的 `in` 腿有**两种身份** ——
 * **退款**（`in` + 支出分类 + 计收支）冲减本期；**还款**（转账 / 分期本金还款，`excludeFromStats`）
 * 是"还上期或分期的钱"，**不冲减本期**。修复前本函数把所有 `in` 腿都当退款 ⇒
 * 还款日落进窗口时「本期应还」被错误抵掉（用户按少的还 → 循环利息）。
 *
 * 🔴 **产品意义（B 域 PRD §5.2.2 原文）**：家盈的账单必须与**银行账单对得上** ——
 * 否则用户按家盈的数字少还钱，产生循环利息。故本函数的窗口是**半开区间**
 * （`[上期账单日, 本期账单日)`，与 `budget.ts` 同源：杜绝边界双计），
 * 且账单日当天的消费**算本期**（含在窗口内）、账单日当天到账的退款**算本期冲抵**。
 *
 * @param input 见 {@link CreditBillInput}
 * @throws RangeError 当日/还款日不在 `[1, 31]` 时（契约层与服务端已挡，此处是最后一道）
 */
export function computeCreditBill(input: CreditBillInput): CreditBill {
  const { billingDay, repaymentDay, today } = input;
  if (
    !Number.isInteger(billingDay) ||
    billingDay < AccountPolicy.BillingDayMin ||
    billingDay > AccountPolicy.BillingDayMax
  ) {
    throw new RangeError(`账单日须在 ${AccountPolicy.BillingDayMin}–${AccountPolicy.BillingDayMax}：${billingDay}`);
  }
  if (
    !Number.isInteger(repaymentDay) ||
    repaymentDay < AccountPolicy.BillingDayMin ||
    repaymentDay > AccountPolicy.BillingDayMax
  ) {
    throw new RangeError(`还款日须在 ${AccountPolicy.BillingDayMin}–${AccountPolicy.BillingDayMax}：${repaymentDay}`);
  }

  // 本期账单日 = 最近一次账单日（≤ 今天；当天算本期）
  const billDay = lastOnOrBefore(today, billingDay);
  // 上期账单日 = **上一个月**的第 `billingDay` 天
  //
  // 🔴 **必须从「配置的 `billingDay`」反算，不能 `addMonths(billDay, -1)`**（`2026-10-12` 真 bug）：
  // `billDay` 已被 `dayInMonth` clamp 成该月实际天数（如 4 月 = 30），再 -1 个月会得
  // `2026-03-30`，而 3 月的账单日其实是 **03-31** ⇒ 窗口 `[03-30, 04-30)` 与上期
  // `[02-28, 03-31)` **在 03-30 / 03-31 两天重叠 ⇒ 消费被双计**。
  // 正确做法是回到"上一个月"再套同一个 `dom`：
  // `dayInMonth(addMonths(today, -1), billingDay)` —— 4 月看 3 月 ⇒ `2026-03-31` ✅ 无缝衔接。
  const windowStart = dayInMonth(addMonths(billDay, -1), billingDay);
  const windowEnd = billDay;

  // 还款日 = 账单日之后的**第一个**还款日（两个日期的先后关系任意：
  // 账单日 5 / 还款日 20 ⇒ 同月 20 日；账单日 20 / 还款日 5 ⇒ 次月 5 日）
  const repaymentDate = nextAfter(billDay, repaymentDay);

  const refundIns: Cents[] = [];
  const repayIns: Cents[] = [];
  const outs: Cents[] = [];
  const afterBillIns: Cents[] = [];
  for (const leg of input.legs) {
    const inWindow = leg.date >= windowStart && leg.date < windowEnd;
    // 🔴 `DR-060` §6（2026-10-24 修既有缺陷）：窗口内 `in` 腿**按「是否退款」分流** ——
    // 退款形态（调用方按 `isRefundTxn` 同判据打的 `refund` 标记）与还款分开记账，
    // 但**两者都进「本期流入」并参与公式**（`DR-064` D1 · 批 113 订正：
    // 2026-10-24 的做法是把还款整段丢掉，导致"上期欠款不结转 + 流入不抵减"两处同时错）。
    //
    // 🔴 优惠腿 / 分期本金腿**不进流入**（`DR-064` D1 / B4）：
    // 前者在公式里是独立减项（`waiver`），后者已被 `principalDeducted` 移出过一次 ⇒
    // 再算作流入就是同一笔钱减两遍。
    const isRefund = leg.direction === LegDirection.In && leg.refund === true;
    if (inWindow) {
      if (leg.direction === LegDirection.Out) {
        outs.push(leg.amount);
        continue;
      }
      if (leg.waiver === true || leg.principal === true) continue;
      if (isRefund) refundIns.push(leg.amount);
      else repayIns.push(leg.amount);
      continue;
    }
    // 账单日之后（含当天）到今天的**退款** ⇒ 退款剔除（下一期）；还款腿不进
    if (leg.date >= windowEnd && leg.date <= today && isRefund) {
      afterBillIns.push(leg.amount);
    }
  }

  const consumed = sumCents(outs);
  const refundedInWindow = sumCents(refundIns);
  const inflowRepayment = sumCents(repayIns);
  const inflow = toCents((refundedInWindow as number) + (inflowRepayment as number));
  const openingBalance = input.openingBalance ?? toCents(0);
  const waiver = input.waiver ?? toCents(0);
  const installment = input.installment;
  if (installment !== undefined) {
    if ((installment.principalDeducted as number) < 0 || (installment.dueThisPeriod as number) < 0) {
      throw new RangeError('分期成对调整不允许负数');
    }
  }
  // `DR-060` §4.3：办理那一期 = 窗口消费 − 分期本金 + 本期分期应还(本金+手续费)；其后各期只有后一项。
  const principalDeducted = installment === undefined ? 0 : (installment.principalDeducted as number);
  const installmentDue = installment === undefined ? 0 : (installment.dueThisPeriod as number);
  // 🔴 `DR-064` D1：本期金额 = 上期余额 + 本期流出 − 本期流入 − 优惠 − 分期移出本金 + 本期分期应还
  const rawDue =
    (openingBalance as number) +
    (consumed as number) -
    (inflow as number) -
    (waiver as number) -
    principalDeducted +
    installmentDue;
  // 冲抵超过消费时账单金额归零（负的部分作为溢缴款结转下期 —— `carryover`）
  const due = toCents(rawDue > 0 ? rawDue : 0);
  const carryover = toCents(rawDue);

  return {
    windowStart,
    windowEnd,
    consumed,
    refundedInWindow,
    openingBalance,
    outflow: consumed,
    inflow,
    inflowRefund: refundedInWindow,
    inflowRepayment,
    carryover,
    due,
    // 🆕 批 `106-B`：实际计入公式的优惠（净额法展示用）—— 0 不出键
    ...((waiver as number) > 0 ? { waiverApplied: waiver } : {}),
    ...(installment === undefined ? {} : { installment }),
    refundsAfterBillDay: sumCents(afterBillIns),
    billDay,
    repaymentDate,
    repaymentWindowStart: billDay,
    repaymentWindowEnd: repaymentDate,
    daysUntilRepayment: daysBetween(today, repaymentDate),
  };
}

// ═══════════════ 信用卡 · 多期账单（批次 `94` · B 域 PRD §5.2 / 原型 `15-债权债务`） ═══════════════

/**
 * **未出账单**：本期账单日**之后**（含当天）到今天的这一截。
 *
 * 与当期账单用的是**同一个半开区间口径**（`[billDay, today]`，右端**含**今天）——
 * 这一段会在下个账单日成为"当期账单"，所以它**不进当期 `due`**，单独展示。
 *
 * 🔴 与 `CreditBill.refundsAfterBillDay` 的关系：后者是**同一窗口内的 `in` 腿合计**
 * （账单日后的退款，记入下期）。本结构把它与消费分列，并给出净额 ——
 * 两者数字必须一致（`consumed − refunded`），本函数**不重算**一遍退款判定。
 */
export interface CreditUnbilled {
  /** 窗口起点 = 本期账单日（含） */
  windowStart: string;
  /** 窗口终点 = 今天（**含**） */
  windowEnd: string;
  /** 窗口内消费（`out` 腿合计）= {@link outflow} 的旧名 */
  consumed: Cents;
  /** 🆕 本期流出（同 {@link consumed}，对账块用的名字） */
  outflow: Cents;
  /** 窗口内**流入**合计（退款 + 还款，不含优惠腿 / 分期本金腿 —— `DR-064` D5） */
  inflow: Cents;
  /** 流入里的退款部分（旧名保留给既有消费者） */
  refunded: Cents;
  /** 🆕 流入里的退款部分（同 {@link refunded}） */
  inflowRefund: Cents;
  /** 🆕 流入里的还款部分 */
  inflowRepayment: Cents;
  /** 净额 = `consumed − inflow`（可为负：退得比花得多、或提前还了钱） */
  net: Cents;
}

/** 一张卡的**多期账单**（当期 + 未出 + 往期） */
export interface CreditBillHistory {
  /** 当期账单（`null` = 未配账期，与 `AccountView.credit.bill` 同义） */
  current: CreditBill | null;
  /** 未出账单（`null` = 未配账期） */
  unbilled: CreditUnbilled | null;
  /**
   * 往期账单，**按时间倒序**（`history[0]` = 上一期）。
   *
   * ⚠️ 每期的 `daysUntilRepayment` / `repaymentDate` 是**相对该期账单日**算的
   * （不是相对今天）—— 往期只用来回答"那期该还多少 / 花了多少"，
   * **不要**拿它们的 `daysUntilRepayment` 渲染"还剩几天"（那是当期的事）。
   */
  history: CreditBill[];
}

export interface CreditBillHistoryInput extends CreditBillInput {
  /** 要回溯的**往期**期数（`0` = 只要当期与未出） */
  months: number;
  /**
   * 🆕 分期成对调整，**按账单日**索引（`DR-060` §4.3）。
   *
   * 由调用方（账户服务）从 `installment_plans` + `loan_schedules` 现算
   * （内核 {@link installmentBillAdjustment}），这里只消费：当期与往期各取
   * `map[billDay]`，未命中 = 该期无分期调整（原口径）。
   * 🔴 未出账单（`unbilled`）**不吃调整** —— 分期扣减发生在"出账"那一刻。
   */
  installmentByBillDay?: Readonly<Record<string, CreditBillInstallment>>;
  /**
   * 🆕 还款优惠，**按账单日**索引（批 `106-B` · `AC-B2.45`）。
   *
   * 与 {@link installmentByBillDay} **完全同模式**：由调用方（账户服务）从
   * `credit_bill_waivers` 现算（Σ `amount` GROUP BY `bill_day`），这里只消费：
   * 当期与往期各取 `map[billDay]`，未命中 = 该期无优惠。
   * 🔴 未出账单（`unbilled`）**不吃优惠** —— 与分期同则（调整只发生在"出账"那一刻）。
   */
  waiverByBillDay?: Readonly<Record<string, Cents>>;
  /**
   * 🆕 **实付还款腿**（批 `106-B` · `AC-B2.57`）—— 🆕 批 `113` 起只喂「账单日后已还」窗口统计
   * （`DR-064` D4），不再喂 {@link allocateRepayments}。
   *
   * 🔴 传入前服务端已做**四类归属**：只有「还款腿」（其余 `in` + 无分类 + 不计收支）
   * 才进这里；优惠腿（`linked_waiver_id` 非空）/ 退款腿 / 分期本金腿**都已被剔除**
   * （`AC-B2.54`）。未传 = 不出 `paid` 键（不臆造"未还"）。
   */
  repaymentLegs?: readonly CreditRepaymentLeg[];
}

/** 往期期数上限 —— 超过一年没有展示价值，且会拖慢查询（12 期足够对账） */
export const CreditHistoryMaxMonths = 12;

/**
 * 🆕 **默认回溯期数 = 6**（批 `106-A` · `AC-B2.32`）。
 *
 * 依据：规格 §8「最近 **6** 期独立 + 更早合并」。
 * 🔴 批 `94` 落地时把它硬写成 3（当时的口头需求是"之前 3 个月"），与规格不符 ⇒ 本批改回 6。
 *
 * 这个常量是**服务端默认 + 端侧展示预期**共用的一个值（会计入账单明细回溯范围的上限，
 * 超出的期次服务端按 AC-B2.63 **不编造金额**）。
 */
export const CreditHistoryDefaultMonths = 6;

/**
 * 算一张卡的**当期 + 未出 + 近 `months` 期**。
 *
 * ## 🔴 为什么往期也调 `computeCreditBill()`
 *
 * 把 `today` 传成"那一期的账单日"，`computeCreditBill` 就会把窗口算成
 * `[上期账单日, 该期账单日)` —— **与当期完全同一套半开区间与月末 clamp 规则**
 * （含"账单日 31 在 2 月按月末"这类边界）。
 *
 * 若另写一份"往前推 N 个月"的窗口计算，就要把 `dayInMonth` / `addMonths` /
 * 跨年 / 农历短月**再实现一遍** —— 那正是 `2026-10-12` 那个"窗口重叠 ⇒ 消费双计"真 bug 的土壤
 * （见 `computeCreditBill` 里 `windowStart` 那段长注释）。
 *
 * ## ⚠️ 诚实登记
 *
 * - 往期账单的 `due` 是**当期口径的复算**（那期消费 − 那期窗口内退款），
 *   **不含"后来才到的退款"**，也**不含用户实际还了多少**（还款是转账腿，
 *   落在卡账户上，不改变账单窗口的聚合）。
 * - 因此「已还 / 未还」状态**不由本函数给**（那要看账户余额与结算流水）。
 */
export function computeCreditBillHistory(input: CreditBillHistoryInput): CreditBillHistory {
  const months = clampInt(input.months, 0, CreditHistoryMaxMonths);
  const currentBillDay = lastOnOrBefore(input.today, input.billingDay);

  // ① 生成**升序**账单日（最早 → 当期）。
  //    🔴 用**配置的 billingDay** 反算上一个月（不能用 `addMonths(cursor, -1)`，
  //    否则短月 clamp 过的日期会与真实账单日错开 —— 同 `computeCreditBill` 里的教训）
  const billDays: string[] = [currentBillDay];
  let cursor = currentBillDay;
  for (let i = 0; i < months; i += 1) {
    cursor = dayInMonth(addMonths(cursor, -1), input.billingDay);
    billDays.push(cursor);
  }
  billDays.reverse();

  // ② 逐期**滚动结转**（`DR-064` D3）—— "上期欠款滚进本期"的唯一实现点。
  //    🔴 最早一期按 0 结转（不臆造更早欠款，`AC-B2.71`）；
  //    🔴 结转到下期用 `carryover`（未 clamp，可为负 = 溢缴款）。
  //    🔴 端侧与服务层**不得再滚动一次**（铁律 24）。
  const ascending: CreditBill[] = [];
  let opening: Cents = toCents(0);
  for (const billDay of billDays) {
    const isCurrent = billDay === currentBillDay;
    const adjustment = input.installmentByBillDay?.[billDay];
    // 当期优惠：调用方未给 map 时退回直传的 `input.waiver`（批 `106-B` 既有行为）；
    // 往期**只认** `waiverByBillDay`（旧代码透传 `input.waiver` 会让同一份优惠被吃 7 次）
    const waiver = isCurrent
      ? (input.waiverByBillDay?.[billDay] ?? input.waiver)
      : input.waiverByBillDay?.[billDay];
    const bill = computeCreditBill({
      billingDay: input.billingDay,
      repaymentDay: input.repaymentDay,
      // 把"今天"设成该期账单日 ⇒ billDay 恰好落在它身上，窗口 = [上期, 该期)
      today: isCurrent ? input.today : billDay,
      legs: input.legs,
      openingBalance: opening,
      ...(waiver === undefined ? {} : { waiver }),
      ...(adjustment === undefined ? {} : { installment: adjustment }),
    });
    ascending.push(bill);
    opening = bill.carryover;
  }

  const current = ascending[ascending.length - 1];
  if (current === undefined) throw new RangeError('账单期次生成失败：至少应有一期');
  const history: CreditBill[] = ascending.slice(0, -1).reverse();

  // 未出账单：本期账单日（含）到今天（含）—— 与当期窗口无缝衔接、不重叠
  // 🔴 `DR-064` D5：与当期**同一套流入口径**（退款 + 还款，不含优惠腿 / 分期本金腿），
  //    但不给 `openingBalance` / `due` / 还款日 —— 它还没成型（`AC-B2.60` 同原则）。
  const outs: Cents[] = [];
  const refundIns: Cents[] = [];
  const repayIns: Cents[] = [];
  for (const leg of input.legs) {
    if (leg.date < current.billDay || leg.date > input.today) continue;
    if (leg.direction === LegDirection.Out) {
      outs.push(leg.amount);
      continue;
    }
    if (leg.waiver === true || leg.principal === true) continue;
    if (leg.refund === true) refundIns.push(leg.amount);
    else repayIns.push(leg.amount);
  }
  const unbilledConsumed = sumCents(outs);
  const unbilledRefund = sumCents(refundIns);
  const unbilledRepay = sumCents(repayIns);
  const unbilledInflow = toCents((unbilledRefund as number) + (unbilledRepay as number));
  const unbilled: CreditUnbilled = {
    windowStart: current.billDay,
    windowEnd: input.today,
    consumed: unbilledConsumed,
    outflow: unbilledConsumed,
    inflow: unbilledInflow,
    refunded: unbilledRefund,
    inflowRefund: unbilledRefund,
    inflowRepayment: unbilledRepay,
    net: toCents((unbilledConsumed as number) - (unbilledInflow as number)),
  };

  /**
   * ③ 已还 / 剩余（`DR-064` D4 · 替换批 `106-B` 的跨期分配）。
   *
   * 🔴 **已还 = 账单日（含）之后的还款**：窗口内的还款已经计入 `inflow`（它减少账单金额），
   *    再算一次"已还"就是同一笔钱减两遍。
   * 右端：当期 = 今天（含，`addDays(today, 1)` 造开区间上界）；往期 = 下一期账单日（不含）。
   */
  if (input.repaymentLegs !== undefined) {
    const upperOfCurrent = addDays(input.today, 1);
    for (let i = 0; i < ascending.length; i += 1) {
      const bill = ascending[i];
      if (bill === undefined) continue;
      const start = bill.billDay;
      const end = ascending[i + 1]?.billDay ?? upperOfCurrent;
      const sums: Cents[] = [];
      for (const r of input.repaymentLegs) {
        if (r.date < start || r.date >= end) continue;
        sums.push(r.amount);
      }
      const repaid = sumCents(sums);
      const status = creditPaidStatusOf(bill.due, repaid);
      const remainingRaw = (bill.due as number) - (repaid as number);
      bill.paid = { repaid, remaining: toCents(remainingRaw > 0 ? remainingRaw : 0), status };
    }
  }

  return { current, unbilled, history };
}
